import {
  UserMeta,
  connectionIdToColor,
  useRoom,
  useSelf,
  useStatus,
  useUpdateMyPresence,
} from './liveblocks-notes.config'
import { LiveblocksYjsProvider } from '@liveblocks/yjs'
import ms from 'ms'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Quill } from 'react-quill-new'
import * as Y from 'yjs'

import { useIntervalCounter } from '@/hooks/use-interval-counter'

import { useTrackCallback } from '@/lib/analytics/events'
import { cn } from '@/lib/ui/style-helpers'

import { countWords, useAssessmentAnalytics } from '@/pages/assessment/analytics'

import { Alert } from '@/components/lemmala-design-system/alert/alert'
import { Loading } from '@/components/lemmala-design-system/loading/loading'

import { RichTextEditor } from './rich-text-editor'

const LONG_DISCONNECTION_DURATION = ms('10s')

type ConnectionErrorBannerProps = {
  isDisconnected: boolean
}

const ConnectionErrorBanner = ({ isDisconnected }: ConnectionErrorBannerProps) => {
  const disconnectedDuration = useIntervalCounter(isDisconnected, ms('1s'))

  if (!isDisconnected) {
    return null
  }

  const isLongDisconnection = disconnectedDuration >= LONG_DISCONNECTION_DURATION

  if (isLongDisconnection) {
    return (
      <Alert variant='destructive'>
        <div className='flex flex-col gap-1'>
          <div className='font-semibold'>
            Connection lost for {Math.floor(disconnectedDuration / 1000)}s
          </div>
          <div className='mt-1 text-xs'>
            Your changes are saved locally. Please check your internet connection or refresh the
            page.
          </div>
        </div>
      </Alert>
    )
  }

  return (
    <Alert variant='warning'>
      <div className='font-semibold'>
        Connection lost. Reconnecting... Your changes are preserved locally.
      </div>
    </Alert>
  )
}

type CollaborativeEditorProps = {
  readonly: boolean
}

export const CollaborativeEditor = ({ readonly }: CollaborativeEditorProps) => {
  const room = useRoom()
  const trackingContext = useAssessmentAnalytics()
  const [text, setText] = useState<Y.Text>()
  const [provider, setProvider] = useState<LiveblocksYjsProvider>()
  const [quillInstance, setQuillInstance] = useState<Quill | null>(null)
  const quillInstanceRef = useRef<Quill | null>(null)
  const yDocRef = useRef<Y.Doc | null>(null)
  const userInfo = useSelf<UserMeta['info']>((me) => me.info)
  const userConnectionId = useSelf<number>((me) => me.connectionId)
  const userColor = useSelf<string>((me) => me.presence.color)
  const status = useStatus()
  const updateMyPresence = useUpdateMyPresence()
  const trackConclusionEdit = useTrackCallback('third-party.assessment.conclusion.edit')
  const trackConclusionFormat = useTrackCallback('third-party.assessment.conclusion.format.apply')
  // Flags whether the user has edited text since the last blur, so we can fire a single
  // analytics event per edit session when focus leaves the editor.
  const hasTrackedEditRef = useRef(false)
  // Snapshot of whether content existed before the current edit session began.
  // Captured on focus so we can report it in the analytics event on blur.
  const hadExistingContentRef = useRef(false)

  useEffect(() => {
    const yDoc = new Y.Doc()
    const yText = yDoc.getText('quill')
    const yProvider = new LiveblocksYjsProvider(room, yDoc)

    yDocRef.current = yDoc
    // oxlint-disable-next-line react/set-state-in-effect
    setText(yText)
    // oxlint-disable-next-line react/set-state-in-effect
    setProvider(yProvider)

    return () => {
      yDoc.destroy()
      yProvider.destroy()
    }
  }, [room])

  useEffect(() => {
    const color = connectionIdToColor(userConnectionId ?? 0)
    updateMyPresence({ color })
  }, [updateMyPresence, userConnectionId])

  useEffect(() => {
    provider?.awareness.setLocalStateField('user', {
      name: userInfo?.name,
      color: userColor,
    })
  }, [provider?.awareness, userColor, userInfo?.name])

  useEffect(() => {
    const quill = quillInstance
    if (!quill || readonly) {
      return undefined
    }

    const handleSelectionChange = (
      range: { index: number; length: number } | null,
      oldRange: { index: number; length: number } | null,
    ) => {
      if (range && !oldRange) {
        hadExistingContentRef.current = countWords(quill.getText()) > 0
      }

      if (!range && oldRange && hasTrackedEditRef.current) {
        hasTrackedEditRef.current = false

        // Track on blur (not mid-keystroke) so the word count reflects the final state.
        if (trackingContext) {
          trackConclusionEdit({
            ...trackingContext,
            hadExistingContent: hadExistingContentRef.current,
            wordCount: countWords(quill.getText()),
          })
        }
      }
    }

    const handleTextChange = (_delta: unknown, _oldDelta: unknown, source: string) => {
      // Only flag that editing happened; the actual analytics call fires on blur.
      if (source === 'user') {
        hasTrackedEditRef.current = true
      }
    }

    quill.on('selection-change', handleSelectionChange)
    quill.on('text-change', handleTextChange)

    return () => {
      quill.off('selection-change', handleSelectionChange)
      quill.off('text-change', handleTextChange)
    }
  }, [quillInstance, readonly, trackConclusionEdit, trackingContext])

  const handleQuillReady = useCallback((quill: Quill) => {
    quillInstanceRef.current = quill
    setQuillInstance(quill)
  }, [])

  const handleFormatApply = useCallback(
    (format: string) => {
      if (!trackingContext) {
        return
      }

      trackConclusionFormat({
        ...trackingContext,
        format,
      })
    },
    [trackConclusionFormat, trackingContext],
  )

  // Show loading only on initial mount before text/provider are ready
  if (!text || !provider) return <Loading lineCount={2} />

  const isConnecting = status === 'initial' || status === 'connecting'
  const isDisconnected = status === 'disconnected'
  const shouldBlur = isConnecting || isDisconnected

  return (
    <div className='flex h-full flex-col gap-2'>
      <ConnectionErrorBanner isDisconnected={isDisconnected} />
      <div className={cn('relative h-full', shouldBlur ? 'opacity-75' : '')}>
        <RichTextEditor
          yText={text}
          provider={provider}
          isReadOnly={readonly}
          onQuillReady={handleQuillReady}
          onFormatApply={handleFormatApply}
        />
      </div>
    </div>
  )
}
